import { pipe, prop, defaultTo, mapObjIndexed, curry, zipObj, map, keys, values, replace, lensProp, view, set, over, nthArg, contains, not, always, F, T, complement, isNil, pathEq, path, __, propEq, or, pathOr } from 'ramda';
import { paramCase, camelCase } from 'change-case';
import { hide, error, success } from 'react-notification-system-redux';
import { getDomain } from '@portal-packages/utils';
import axios from 'axios';
/**
 * Contains keys pointing properties on the `selectors` object of `itBehavesLikeAnAPIReducer`.
 *
 * @example
 * ```ts
 * describe('Example Reducer', () => {
 *   itBehavesLikeAnAPIReducer({
 *     entity: EXAMPLE_ENTITY,
 *     exampleData: {},
 *     selectors: {
 *       [API_META_TYPES.IS_LOADING]: isExampleLoadingSelector,
 *       [API_META_TYPES.LOADED]: isExampleLoadedSelector,
 *       [API_META_TYPES.DATA]: examplesSelector,
 *       [API_META_TYPES.ERROR]: hasErrorExampleSelector,
 *     },
 *   });
 * });
 * ```
 * @public
 */

var API_META_TYPES;

(function (API_META_TYPES) {
  API_META_TYPES["DATA"] = "DATA";
  API_META_TYPES["HEADERS"] = "HEADERS";
  API_META_TYPES["META"] = "META";
  API_META_TYPES["IS_LOADING"] = "IS_LOADING";
  API_META_TYPES["LOADED"] = "LOADED";
  API_META_TYPES["ERROR"] = "ERROR";
})(API_META_TYPES || (API_META_TYPES = {}));
/**
 * Contains keys pointing to all the properties of a {@link APIState} object.
 *
 * @example
 * ```ts
 * const defaultState = {};
 *
 * export default (state = defaultState, action) => {
 *   switch (action.type) {
 *     case apiRequestedActionType(GET_EXAMPLE): {
 *       return {
 *         ...apiLoadingState(state),
 *         [API_META_REDUCER_ATTRIBUTES.LOADED]: true, // overriding loaded to infinite scroll
 *       };
 *     }
 *     default:
 *       return state;
 *   }
 * };
 * ```
 * @public
 */


var API_META_REDUCER_ATTRIBUTES;

(function (API_META_REDUCER_ATTRIBUTES) {
  API_META_REDUCER_ATTRIBUTES["DATA"] = "data";
  API_META_REDUCER_ATTRIBUTES["HEADERS"] = "headers";
  API_META_REDUCER_ATTRIBUTES["META"] = "meta";
  API_META_REDUCER_ATTRIBUTES["IS_LOADING"] = "isLoading";
  API_META_REDUCER_ATTRIBUTES["LOADED"] = "loaded";
  API_META_REDUCER_ATTRIBUTES["ERROR"] = "error";
})(API_META_REDUCER_ATTRIBUTES || (API_META_REDUCER_ATTRIBUTES = {}));
/**
 * Returns the "data" portion of a {@link APIState} object.
 *
 * @typeParam TResponse - The response body type.
 *
 * @public
 */


var getAPIDataSelector = function getAPIDataSelector(inputSelector) {
  return pipe(inputSelector, prop(API_META_REDUCER_ATTRIBUTES.DATA));
};
/**
 * Returns the "data" portion of a {@link APIState} object.
 * Will return a fallback value if the value in the state is a nil value.
 *
 * @typeParam TResponse - The response body type.
 *
 * @public
 */


var getAPIDataSelectorOr = function getAPIDataSelectorOr(inputSelector, fallbackValue) {
  if (fallbackValue === void 0) {
    fallbackValue = {};
  }

  return pipe(getAPIDataSelector(inputSelector), defaultTo(fallbackValue));
};
/**
 * Returns the "headers" portion of a {@link APIState} object.
 *
 * NOTE: the headers keys already have been converted using using {@link convertResponseHeaders}.
 * See {@link convertResponseHeaders} for details.
 *
 * @public
 */


var getAPIHeadersSelector = function getAPIHeadersSelector(inputSelector) {
  return pipe(inputSelector, prop(API_META_REDUCER_ATTRIBUTES.HEADERS));
};
/**
 * Returns the "is loading" portion of a {@link APIState} object.
 *
 * @public
 */


var isLoadingAPISelector = function isLoadingAPISelector(inputSelector) {
  return pipe(inputSelector, prop(API_META_REDUCER_ATTRIBUTES.IS_LOADING));
};
/**
 * Returns the "has loaded" portion of a {@link APIState} object.
 *
 * @public
 */


var hasLoadedAPISelector = function hasLoadedAPISelector(inputSelector) {
  return pipe(inputSelector, prop(API_META_REDUCER_ATTRIBUTES.LOADED));
};
/**
 * Returns the "error" portion of a {@link APIState} object.
 *
 * @public
 */


var getAPIErrorSelector = function getAPIErrorSelector(inputSelector) {
  return pipe(inputSelector, prop(API_META_REDUCER_ATTRIBUTES.ERROR));
};
/**
 * Returns `true` if the {@link APIState} object have a error currently.
 *
 * @public
 */


var hasErrorAPISelector = function hasErrorAPISelector(inputSelector) {
  return pipe(getAPIErrorSelector(inputSelector), Boolean);
};
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

/* global Reflect, Promise */


var _extendStatics = function extendStatics(d, b) {
  _extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) {
      if (b.hasOwnProperty(p)) d[p] = b[p];
    }
  };

  return _extendStatics(d, b);
};

function __extends(d, b) {
  _extendStatics(d, b);

  function __() {
    this.constructor = d;
  }

  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};
/**
 * All the api status types.
 *
 * @public
 */


var API_STATUS_TYPES;

(function (API_STATUS_TYPES) {
  API_STATUS_TYPES["REQUESTED"] = "REQUESTED";
  API_STATUS_TYPES["SUCCESS"] = "SUCCESS";
  API_STATUS_TYPES["ERROR"] = "ERROR";
})(API_STATUS_TYPES || (API_STATUS_TYPES = {}));
/**
 * All the api status action suffixes.
 *
 * @public
 */


var API_STATUS_ACTION_SUFFIXES = mapObjIndexed(function (value) {
  return paramCase(value);
}, API_STATUS_TYPES);
var mapKeys = curry(function (fn, obj) {
  return zipObj(map(fn, keys(obj)), values(obj));
});

var getActionType = function getActionType(entity, type) {
  return entity + "-" + API_STATUS_ACTION_SUFFIXES[type];
};
/**
 * Builds the "requested" redux action type.
 *
 * @param entity - The request entity name.
 *
 * @public
 */


var apiRequestedActionType = function apiRequestedActionType(entity) {
  return getActionType(entity, API_STATUS_TYPES.REQUESTED);
};
/**
 * Builds the "error" redux action type.
 *
 * @param entity - The request entity name.
 *
 * @public
 */


var apiErrorActionType = function apiErrorActionType(entity) {
  return getActionType(entity, API_STATUS_TYPES.ERROR);
};
/**
 * Builds the "success" redux action type.
 *
 * @param entity - The request entity name.
 *
 * @public
 */


var apiSuccessActionType = function apiSuccessActionType(entity) {
  return getActionType(entity, API_STATUS_TYPES.SUCCESS);
};
/**
 * Converts the response headers names to
 * javascript object keys.
 *
 * Examples:
 * `X-Powered-By` -\> `poweredBy`,
 * `Content-Type` -\> `contentType`.
 *
 * @param headers - The headers to be converted.
 *
 * @public
 */


var convertResponseHeaders = function convertResponseHeaders(headers) {
  return mapKeys(pipe(replace('x-', ''), camelCase), headers);
};
/**
 * Builds the "requested" redux action.
 *
 * @param entity - The request entity name.
 * @param meta - The request metadata.
 *
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiRequestedAction = function apiRequestedAction(entity, meta) {
  var _a;

  return _a = {
    type: apiRequestedActionType(entity)
  }, _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a;
};
/**
 * Builds the "success" redux action.
 *
 * @param entity - The request entity name.
 * @param data - The response data.
 * @param headers - The response headers.
 * @param meta - The request metadata.
 *
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiSuccessAction = function apiSuccessAction(entity, data, headers, meta) {
  var _a;

  return _a = {
    type: apiSuccessActionType(entity)
  }, _a[API_META_REDUCER_ATTRIBUTES.DATA] = data, _a[API_META_REDUCER_ATTRIBUTES.HEADERS] = convertResponseHeaders(headers), _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a;
};
/**
 * Builds the "error" redux action.
 *
 * @param entity - The request entity name.
 * @param error - The axios error.
 * @param meta - The request metadata.
 *
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiErrorAction = function apiErrorAction(entity, error, meta) {
  var _a;

  return _a = {
    type: apiErrorActionType(entity)
  }, _a[API_META_REDUCER_ATTRIBUTES.ERROR] = error, _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a;
};
/**
 * Builds an {@link APIState} object in the initial state.
 *
 * @param defaultData - Default value for the `data` state. Defaults to `{}`.
 * @param defaultHeaders - Default value for the `headers` state. Defaults to `{}`.
 * @param defaultMeta - Default value for the `meta` state. Defaults to `{}`.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiInitialState = function apiInitialState(defaultData, defaultHeaders, defaultMeta) {
  var _a;

  if (defaultData === void 0) {
    defaultData = {};
  }

  if (defaultHeaders === void 0) {
    defaultHeaders = {};
  }

  if (defaultMeta === void 0) {
    defaultMeta = {};
  }

  return _a = {}, _a[API_META_REDUCER_ATTRIBUTES.IS_LOADING] = false, _a[API_META_REDUCER_ATTRIBUTES.LOADED] = false, _a[API_META_REDUCER_ATTRIBUTES.DATA] = defaultData, _a[API_META_REDUCER_ATTRIBUTES.HEADERS] = defaultHeaders, _a[API_META_REDUCER_ATTRIBUTES.ERROR] = '', _a[API_META_REDUCER_ATTRIBUTES.META] = defaultMeta, _a;
};
/**
 * Builds an {@link APIState} object in the loading state.
 *
 * @param state - The previous state to merge with
 * @param meta - The request metadata
 * @param keepLoadedOnLoadingState - Whenever it should keep the current
 * value of the `loaded` field, default to `false`.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiLoadingState = function apiLoadingState(state, meta, keepLoadedOnLoadingState) {
  var _a;

  if (meta === void 0) {
    meta = {};
  }

  return _assign(_assign({}, state), (_a = {}, _a[API_META_REDUCER_ATTRIBUTES.IS_LOADING] = true, _a[API_META_REDUCER_ATTRIBUTES.LOADED] = keepLoadedOnLoadingState ? state.loaded : false, _a[API_META_REDUCER_ATTRIBUTES.ERROR] = '', _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a));
};
/**
 * Builds an {@link APIState} object in the success state.
 *
 * @param data - The response data
 * @param headers - The response headers.
 *
 * NOTE: the headers keys already should have been converted using using {@link convertResponseHeaders}.
 * See {@link convertResponseHeaders} for details.
 *
 * @param meta - The request metadata.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiSuccessState = function apiSuccessState(data, headers, meta) {
  var _a;

  if (meta === void 0) {
    meta = {};
  }

  return _a = {}, _a[API_META_REDUCER_ATTRIBUTES.IS_LOADING] = false, _a[API_META_REDUCER_ATTRIBUTES.LOADED] = true, _a[API_META_REDUCER_ATTRIBUTES.DATA] = data, _a[API_META_REDUCER_ATTRIBUTES.HEADERS] = headers, _a[API_META_REDUCER_ATTRIBUTES.ERROR] = '', _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a;
};
/**
 * Builds an {@link APIState} object in the error state.
 *
 * @param state - The previous state to merge with.
 * @param error - The axios error object.
 * @param meta - The request metadata.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */


var apiErrorState = function apiErrorState(state, error, meta) {
  var _a;

  if (meta === void 0) {
    meta = {};
  }

  return _assign(_assign({}, state), (_a = {}, _a[API_META_REDUCER_ATTRIBUTES.IS_LOADING] = false, _a[API_META_REDUCER_ATTRIBUTES.LOADED] = false, _a[API_META_REDUCER_ATTRIBUTES.ERROR] = error, _a[API_META_REDUCER_ATTRIBUTES.META] = meta, _a));
};

var dataLens = lensProp(API_META_REDUCER_ATTRIBUTES.DATA);
/**
 * @public
 */

var getData = view(dataLens);
/**
 * @public
 */

var setData = set(dataLens);
/**
 * @public
 */

var overData = over(dataLens);
var mapLoadingStateWithDefaultState = nthArg(2);
/**
 * Builds an API Reducer
 *
 * @param params - A {@link APIReducerParams} object, see {@link APIReducerParams} for details.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The request metadata type.
 *
 * @public
 */

var apiReducerCreator = function apiReducerCreator(_a) {
  var entity = _a.entity,
      defaultData = _a.defaultData,
      defaultHeaders = _a.defaultHeaders,
      defaultMeta = _a.defaultMeta,
      _b = _a.mapLoadingState,
      mapLoadingState = _b === void 0 ? mapLoadingStateWithDefaultState : _b,
      _c = _a.mapSuccessData,
      mapSuccessData = _c === void 0 ? prop(API_META_REDUCER_ATTRIBUTES.DATA) : _c,
      _d = _a.resetActions,
      resetActions = _d === void 0 ? [] : _d;
  var defaultState = apiInitialState(defaultData, defaultHeaders, defaultMeta);
  return function (state, action) {
    if (state === void 0) {
      state = defaultState;
    }

    if (contains(action.type, resetActions)) {
      return defaultState;
    }

    switch (action.type) {
      case apiRequestedActionType(entity):
        {
          return apiLoadingState(mapLoadingState(action, state, defaultState), action[API_META_REDUCER_ATTRIBUTES.META]);
        }

      case apiSuccessActionType(entity):
        {
          return apiSuccessState(mapSuccessData(action, state), action[API_META_REDUCER_ATTRIBUTES.HEADERS], action[API_META_REDUCER_ATTRIBUTES.META]);
        }

      case apiErrorActionType(entity):
        {
          return apiErrorState(state, action.error, action[API_META_REDUCER_ATTRIBUTES.META]);
        }

      default:
        {
          return state;
        }
    }
  };
};
/**
 * Time (in seconds) in which the success notifications will
 * be dismissed automatically.
 *
 * @public
 */


var SUCCESS_NOTIFICATION_AUTO_DISMISS = 4;
/**
 * Key used on the redux actions to differentiate
 * api requests from normal requests on the middleware.
 *
 * See also: {@link isAPIRequestAction}.
 *
 * @public
 */

var API_REQUEST = 'api-request';
/**
 * Supported HTTP methods.
 *
 * @public
 */

var API_REQUEST_HTTP_METHODS;

(function (API_REQUEST_HTTP_METHODS) {
  API_REQUEST_HTTP_METHODS["GET"] = "GET";
  API_REQUEST_HTTP_METHODS["POST"] = "POST";
  API_REQUEST_HTTP_METHODS["PUT"] = "PUT";
  API_REQUEST_HTTP_METHODS["PATCH"] = "PATCH";
  API_REQUEST_HTTP_METHODS["DELETE"] = "DELETE";
})(API_REQUEST_HTTP_METHODS || (API_REQUEST_HTTP_METHODS = {}));
/**
 * Alias to console.error;
 *
 * @public
 */


var logError = console.error; // eslint-disable-line no-console

var containsTimeout = contains('timeout');
var containsNetworkError = contains('Network Error');
/**
 * Tests if an error is a timeout error.
 *
 * @param error - The `AxiosError` object.
 *
 * @public
 */

var isTimeoutError = function isTimeoutError(error) {
  return containsTimeout(error.toString()) || error.code === 'ECONNABORTED';
};
/**
 * Tests if an error is a network error.
 *
 * @param error - The `AxiosError` object.
 *
 * @public
 */


var isNetworkError = function isNetworkError(error) {
  return containsNetworkError(error.toString());
};
/**
 * Tests if an error should be logged to the console.
 * Only network errors and timeout errors will return `true`.
 *
 * @param error - The `AxiosError` object.
 *
 * @public
 */


var shouldLogError = function shouldLogError(error) {
  return isNetworkError(error) || isTimeoutError(error);
};
/**
 * Adds the endpoint name and method to the error message.
 *
 * @param error - The `AxiosError` object.
 *
 * @public
 */


var addEndpointInErrorMessage = function addEndpointInErrorMessage(error) {
  var _a = error.config,
      method = _a.method,
      url = _a.url;
  var message = error.message + " At: " + method + " " + url;
  var errorWithEndpoint = Object.assign(error.constructor(), error);
  errorWithEndpoint.message = message;
  return errorWithEndpoint;
};
/**
 * Adds a interceptor to the given `instance` axios instance.
 *
 * The interceptor will add the endpoint name to the error message.
 *
 * @param instance - The axios instance to add the interceptors.
 *
 * @public
 */


var addInterceptors = function addInterceptors(instance) {
  instance.interceptors.response.use(undefined, function (error) {
    if (shouldLogError(error) && error.config) {
      return Promise.reject(addEndpointInErrorMessage(error));
    }

    return Promise.reject(error);
  });
};

var defaultToObject = defaultTo({});
var defaultToNull = defaultTo(null);
/**
 * This error is supposed to be thrown on request response validation functions.
 *
 * See also: {@link RawAPIRequestParams} `validation` field.
 *
 * @example
 * ```ts
 * const checkResponse = (response: AxiosResponse) => {
 *   if (!response.headers['x-some-header']) {
 *      throw new ValidationError('Missing x-some-header header');
 *   }
 *
 *   if (!response.data) {
 *      throw new ValidationError('Missing response body');
 *   }
 *
 *   if (!response.data.value < 50) {
 *      throw new ValidationError('Value is outside desired range');
 *   }
 * };
 * ```
 * @public
 */

var ValidationError =
/** @class */
function (_super) {
  __extends(ValidationError, _super);

  function ValidationError() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.__proto__ = Error;
    /**
     * Public flag to help testing if a given error is a {@link ValidationError};
     *
     * @readonly
     */

    _this.VALIDATION_ERROR = true;
    return _this;
  }

  return ValidationError;
}(Error);
/**
 * Basic error handler, it's main purpose is to be used with chainable api requests
 * as the promise rejection handler.
 *
 * It will log the error if the error was generated from a network error or timeout error.
 * It will re-throw the error if the error wasn't caused by the server or other network related
 * issues, so if you expect the request to throw for any other reason (eg: you throw a
 * {@link ValidationError}), then you will need to re-catch to avoid a uncaught promise rejection.
 *
 * @param error - The error to handle.
 *
 * @example
 * ```
 * // In this case we throw a validation error
 * // inside `getExampleValidation`
 * getExampleValidation('2')
 *    .then(updateState)
 *    .catch(ignoreAPIRequestError)
 *    .catch((error) => {
 *      if (error.VALIDATION_ERROR) {
 *        // Handle the expected cases correctly, for example:
 *        // inform the server, retry the request, show a notification, etc...
 *      } else {
 *        // For any other unknown cases you might want to re-throw the
 *        // error, as it is in fact unexpected, and should be
 *        // reported properly.
 *        throw error;
 *      }
 *    });
 * ```
 * When you don't expect any other errors:
 * ```ts
 * getExample('2')
 *    .then(updateState)
 *    .catch(ignoreAPIRequestError);
 *    // no re-catch needed
 *    // `ignoreAPIRequestError` will already log network errors
 *    // and throw for unexpected errors.
 * ```
 *
 * @public
 */


var ignoreAPIRequestError = function ignoreAPIRequestError(error) {
  if (!(error instanceof ValidationError)) {
    var _a = defaultToObject(error),
        request = _a.request,
        response = _a.response,
        config = _a.config;

    var url = defaultToObject(config).url;
    var isRequestError = Boolean(url || request || response);

    if (shouldLogError(error)) {
      logError(error);
    }

    if (not(isRequestError)) {
      // In case the error is "undefined"
      throw defaultToNull(error);
    }
  }
};

var apiRequest$1 = function apiRequest$1(_a) {
  var client = _a.client,
      entity = _a.entity,
      meta = _a.meta,
      method = _a.method,
      url = _a.url,
      data = _a.data,
      config = _a.config,
      shouldNotifyError = _a.shouldNotifyError,
      shouldNotifySuccess = _a.shouldNotifySuccess,
      shouldStoreData = _a.shouldStoreData,
      isChainable = _a.isChainable,
      _b = _a.isReloadable,
      isReloadable = _b === void 0 ? false : _b,
      validation = _a.validation;
  return function (dispatch) {
    var promise = new Promise(function (resolve, reject) {
      return dispatch({
        type: API_REQUEST,
        payload: {
          client: client,
          entity: entity,
          meta: meta,
          request: {
            method: method,
            url: url,
            data: data,
            config: config
          },
          shouldNotifyError: shouldNotifyError,
          shouldNotifySuccess: shouldNotifySuccess,
          shouldStoreData: shouldStoreData,
          isReloadable: isReloadable,
          onSuccess: resolve,
          onError: reject,
          validation: validation
        }
      });
    });

    if (!isChainable) {
      promise.catch(ignoreAPIRequestError);
      return null;
    }

    return promise;
  };
};
/**
 * Issues a `GET` request.
 *
 * Resolves with the `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * @param params - The request configuration, see {@link APIRequestParams} for details on how to configure the request.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The meta field type.
 * @typeParam TChainable - Generic type used to decide if the request function
 * should return a promise or not, it's inferred automatically.
 *
 * @example
 * ```ts
 * type TResponse = {
 *   serverResponseData: string,
 * };
 *
 * type Meta = {
 *   test: boolean;
 * }
 *
 * const getExample = (id: string, meta: Meta): APIRequestResult<Response> =>
 *   apiGet<void, Response, Meta>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     meta,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * store.dispatch(getExample('2'), { test: true })
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 *
 * @public
 */


var apiGet = function apiGet(params) {
  return apiRequest$1(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.GET,
    data: undefined
  }));
};
/**
 * Issues a `POST` request.
 *
 * Resolves with the `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * @param params - The request configuration, see {@link APIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRequestBody - The request body type.
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The meta field type.
 * @typeParam TChainable - Generic type used to decide if the request function
 * should return a promise or not, it's inferred automatically.
 *
 * @example
 * ```ts
 * type Response = {
 *   serverResponseData: string,
 * };
 *
 * type Body = {
 *   clientData: string,
 * };
 *
 * type Meta = {
 *   test: boolean;
 * };
 *
 * const postExample = (data: Body, meta: Meta): APIRequestResult<TResponse> =>
 *   apiPost<Body, Response, Meta>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *     meta,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * store.dispatch(postExample({ clientData: 2 }), { test: 0 })
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 *
 * @public
 */


var apiPost = function apiPost(params) {
  return apiRequest$1(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.POST
  }));
};
/**
 * Issues a `POST` request.
 *
 * Resolves with the `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * @param params - The request configuration, see {@link APIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRequestBody - The request body type.
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The meta field type.
 * @typeParam TChainable - Generic type used to decide if the request function
 * should return a promise or not, it's inferred automatically.
 *
 * @example
 * ```ts
 * type Response = {
 *   serverResponseData: string,
 * };
 *
 * type Body = {
 *   clientData: string,
 * };
 *
 * type Meta = {
 *   test: boolean;
 * };
 *
 * const putExample = (data: Body, meta: Meta): APIRequestResult<TResponse> =>
 *   apiPut<Body, Response, Meta>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *     meta,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * store.dispatch(putExample({ clientData: 2 }), { test: 0 })
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 *
 * @public
 */


var apiPut = function apiPut(params) {
  return apiRequest$1(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.PUT
  }));
};
/**
 * Issues a `POST` request.
 *
 * Resolves with the `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * @param params - The request configuration, see {@link APIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRequestBody - The request body type.
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The meta field type.
 * @typeParam TChainable - Generic type used to decide if the request function
 * should return a promise or not, it's inferred automatically.
 *
 * @example
 * ```ts
 * type Response = {
 *   serverResponseData: string,
 * };
 *
 * type Body = {
 *   clientData: string,
 * };
 *
 * type Meta = {
 *   test: boolean;
 * };
 *
 * const patchExample = (data: Body, meta: Meta): APIRequestResult<TResponse> =>
 *   apiPatch<Body, Response, Meta>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *     meta,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * store.dispatch(patchExample({ clientData: 2 }), { test: 0 })
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 *
 * @public
 */


var apiPatch = function apiPatch(params) {
  return apiRequest$1(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.PATCH
  }));
};
/**
 * Issues a `DELETE` request.
 *
 * Resolves with the `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * @param params - The request configuration, see {@link APIRequestParams} for details on how to configure the request.
 *
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The meta field type.
 * @typeParam TChainable - Generic type used to decide if the request function
 * should return a promise or not, it's inferred automatically.
 *
 * @example
 * ```ts
 * type TResponse = {
 *   serverResponseData: string,
 * };
 *
 * type Meta = {
 *   test: boolean;
 * };
 *
 * const deleteExample = (id: string, meta: Meta): APIRequestResult<Response> =>
 *   apiDelete<void, Response, Meta>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     meta,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * store.dispatch(deleteExample('2', { test: true }))
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 *
 * @public
 */


var apiDelete = function apiDelete(params) {
  return apiRequest$1(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.DELETE,
    data: undefined
  }));
};
/**
 * Tests if an response status code is a successful one,
 * eg: any `2xx`.
 *
 * @param status - The status code.
 *
 * @public
 */


var validateAPIResponseStatus = function validateAPIResponseStatus(status) {
  return status.toString().indexOf('2') === 0;
};
/**
 * Creates an axios instance and attach the interceptors required.
 *
 * Note: when enabling `withCredentials`, make sure that the API has a large total header
 * size to fit cookies properly.
 *
 * See also: {@link APIClientMap}, {@link apiRequestMiddleware}, {@link createRawAPIClient}.
 *
 * @param config - An Axios client config
 * @public
 */


var createHttpClient = function createHttpClient(config) {
  var _a;

  if (config === void 0) {
    config = {};
  }

  var instance = axios.create(_assign(_assign({}, config), {
    baseURL: (_a = config.baseURL) === null || _a === void 0 ? void 0 : _a.replace('{DOMAIN}', getDomain()),
    validateStatus: validateAPIResponseStatus
  }));
  addInterceptors(instance);
  return instance;
};
/**
 * Utility for issuing an API request.
 *
 * @param apiClient - An `AxiosInstance` object.
 * @param method - The request method, see {@link API_REQUEST_HTTP_METHODS} for details.
 * @param url - The URL of the request, (NOTE: it will be appended to the end of the `apiClient`'s base url, see {@link createHttpClient} for details.)
 * @param data - The request body data.
 * @param config - An `AxiosRequestConfig` object. see Axios official documentation for details.
 *
 * @public
 */


var baseAPIRequest = function baseAPIRequest(apiClient, method, url, data, config) {
  switch (method) {
    case API_REQUEST_HTTP_METHODS.GET:
      return apiClient.get(url, config);

    case API_REQUEST_HTTP_METHODS.POST:
      return apiClient.post(url, data, config);

    case API_REQUEST_HTTP_METHODS.PUT:
      return apiClient.put(url, data, config);

    case API_REQUEST_HTTP_METHODS.PATCH:
      return apiClient.patch(url, data, config);

    case API_REQUEST_HTTP_METHODS.DELETE:
      return apiClient.delete(url, config);

    default:
      throw new Error("Unexpected request method: " + method);
  }
};

var unauthorizedStatusCodePattern = /401/;
/**
 * Returns true if the error was caused by a HTTP 401 response.
 *
 * NOTE: It actually tests if the error string contains the 401 pattern
 * so errors with numbers on them might return false positives.
 *
 * @param error - The `AxiosError` result of the failed request.
 *
 * @public
 */

var isAPIRequestUnauthorized = function isAPIRequestUnauthorized(error) {
  return unauthorizedStatusCodePattern.test(error.message);
};
/**
 * Tests if the given redux action is an action built by
 * the api request functions.
 *
 * @param param - A redux action to test
 *
 * @public
 */


var isAPIRequestAction = function isAPIRequestAction(_a) {
  var type = _a.type;
  return type === API_REQUEST;
};
/**
 * Will execute the request on the `client` axios instance using
 * the configuration provided in the `payload`. It is the common
 * behavior shared between the {@link apiRequestMiddleware} and requests
 * made using raw api calls.
 *
 * Behavior:
 * - Request lifecycle callbacks (`onRequested`, `onSuccess`, `onError`) are called depending
 * on the request result.
 *   - `onRequested` is always called.
 *   - `onError` and `onSuccess` are only called depending on whenever the request was
 * successful or not.
 * - If the request is successful a validation function will be called, if it throws, the
 * request promise will be reject and no success callbacks will be called.
 *
 * @param client - The axios instance to use on the request.
 * @param payload - a APIRequestPayload object, see APIRequestPayload for details.
 *
 * @typeParam TRequestBody - The request body type.
 * @typeParam TResponse - The response body type.
 * @typeParam TMeta - The payload metadata type.
 *
 * @public
 */


var requestManager = function requestManager(client, payload, _a) {
  var _b = _a === void 0 ? {} : _a,
      onRequested = _b.onRequested,
      onRequestSuccess = _b.onSuccess,
      onRequestError = _b.onError,
      onNotifySuccess = _b.onNotifySuccess,
      onNotifyError = _b.onNotifyError;

  var _c = payload.request,
      config = _c.config,
      data = _c.data,
      method = _c.method,
      url = _c.url,
      onSuccess = payload.onSuccess,
      onError = payload.onError,
      shouldNotifyError = payload.shouldNotifyError,
      shouldNotifySuccess = payload.shouldNotifySuccess,
      shouldStoreData = payload.shouldStoreData,
      validation = payload.validation;
  onRequested === null || onRequested === void 0 ? void 0 : onRequested(payload);
  var shouldNotifySuccessFn = shouldNotifySuccess || F;
  var shouldNotifyErrorFn = shouldNotifyError || T;
  var shouldStoreDataFn = shouldStoreData || T;
  var validationFn = validation || always(undefined);
  var requestPromise = baseAPIRequest(client, method, url, data, config);
  return requestPromise.then(function (response) {
    validationFn === null || validationFn === void 0 ? void 0 : validationFn(response);
    onRequestSuccess === null || onRequestSuccess === void 0 ? void 0 : onRequestSuccess(payload, (shouldStoreDataFn === null || shouldStoreDataFn === void 0 ? void 0 : shouldStoreDataFn(response)) ? response.data : null, response.headers);

    if (shouldNotifySuccessFn === null || shouldNotifySuccessFn === void 0 ? void 0 : shouldNotifySuccessFn(response)) {
      onNotifySuccess === null || onNotifySuccess === void 0 ? void 0 : onNotifySuccess(payload);
    }

    onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(response);
  }).catch(function (error) {
    onRequestError === null || onRequestError === void 0 ? void 0 : onRequestError(payload, error);

    if (shouldNotifyErrorFn === null || shouldNotifyErrorFn === void 0 ? void 0 : shouldNotifyErrorFn(error)) {
      onNotifyError === null || onNotifyError === void 0 ? void 0 : onNotifyError(payload, error);
    }

    onError === null || onError === void 0 ? void 0 : onError(error);
  });
};

var isNotNil = complement(isNil);
var hasNoResponse = pathEq(['response'], undefined);
var getResponseStatus = path(['response', 'status']);
var isServiceUnavailable = pipe(getResponseStatus, contains(__, [408, 503]));
var isGETRequest = propEq('method', API_REQUEST_HTTP_METHODS.GET);

var shouldRetryWhenNotReloadable = function shouldRetryWhenNotReloadable(error, request) {
  return isGETRequest(request) && or(hasNoResponse(error), isServiceUnavailable(error));
};

var createReloadFunction = function createReloadFunction(dispatch, actionType, action) {
  return function () {
    dispatch(hide(actionType));
    dispatch(action);
  };
};

var shouldRetry = function shouldRetry(error, _a) {
  var isReloadable = _a.isReloadable,
      request = _a.request;
  return isReloadable || shouldRetryWhenNotReloadable(error, request);
};
/**
 * Builds a reload function, when called it will clear
 * any error notifications from the action entity and
 * send the request again.
 *
 * @param dispatch - A redux store dispatch function.
 * @param error - The `AxiosError` error, result of the request.
 * @param actionType - The action type string, see {@link apiErrorActionType} for details.
 * @param action - The {@link APIRequestAction} object which initiated the request.
 *
 * @public
 */


var getAPIRetryFunction = function getAPIRetryFunction(dispatch, error, actionType, action) {
  return isNotNil(action) && shouldRetry(error, action.payload) ? createReloadFunction(dispatch, actionType, action) : null;
};
/**
 * Dispatches an error notification action, it uses
 * `react-notification-system-redux` to build the action.
 *
 * @param entity - The request entity name.
 * @param error - The `AxiosError` error, result of the request.
 * @param action - The {@link APIRequestAction} object which initiated the request.
 *
 * @public
 */


var notifyAPIError = function notifyAPIError(entity, error$1, action) {
  return function (dispatch) {
    var actionType = apiErrorActionType(entity);
    var message = pathOr("error.request." + entity, ['response', 'data', 'code'], error$1);
    var retryFunc = getAPIRetryFunction(dispatch, error$1, actionType, action);
    return dispatch(error({
      uid: actionType,
      message: message,
      autoDismiss: 0,
      // @ts-ignore
      retryFunc: retryFunc
    }));
  };
};
/**
 * Dispatches an success notification action, it uses
 * `react-notification-system-redux` to build the action.
 *
 * @param entity - The request entity name.
 *
 * @public
 */


var notifyAPISuccess = function notifyAPISuccess(entity) {
  return success({
    uid: apiSuccessActionType(entity),
    message: "success." + entity,
    autoDismiss: SUCCESS_NOTIFICATION_AUTO_DISMISS
  });
};
/**
 * Builds a redux middleware for processing the requests issued by the functions
 * {@link apiGet}, {@link apiPut}, {@link apiPost}, {@link apiPatch} and {@link apiDelete}.
 *
 * @example
 * ```ts
 * const apiMiddleware = apiRequestMiddleware(API_CLIENTS);
 *
 * const store = createStore(reducers, applyMiddleware(apiMiddleware));
 *
 * store.dispatch(apiGet({ entity, url, client }))
 * ```
 *
 * @param clients - Contains the `AxiosInstance` instances used in the requests, see also: {@link APIClientMap}.
 *
 * @public
 */


var apiRequestMiddleware = function apiRequestMiddleware(clients) {
  return function (store) {
    return function (next) {
      return function (action) {
        if (action.type === API_REQUEST) {
          var client = action.payload.client;
          requestManager(clients[client], action.payload, {
            onRequested: function onRequested(payload) {
              return store.dispatch(apiRequestedAction(payload.entity, payload.meta));
            },
            onSuccess: function onSuccess(payload, data, headers) {
              return store.dispatch(apiSuccessAction(payload.entity, data, headers, payload.meta));
            },
            onError: function onError(payload, error) {
              return store.dispatch(apiErrorAction(payload.entity, error, payload.meta));
            },
            onNotifySuccess: function onNotifySuccess(payload) {
              return store.dispatch(notifyAPISuccess(payload.entity));
            },
            onNotifyError: function onNotifyError(payload, error) {
              return store.dispatch(notifyAPIError(payload.entity, error, action));
            }
          });
        }

        return next(action);
      };
    };
  };
};
/**
 * Creates a API client capable of issuing raw API requests without using an
 * pre existing redux stack (middlewares, selectors, action creators) to
 * manage the request status and run lifecycle callbacks.
 * Instead all callbacks are concentrated on the client itself.
 *
 * It's the equivalent of {@link createHttpClient} for raw API Calls.
 *
 * @param config - See AxiosRequestConfig for details.
 * @param lifecycleCallbacks - See {@link RequestLifecycleCallbacks} for details.
 *
 * @example
 * ```ts
 * const client = createRawAPIClient({ baseURL: '/' });
 *
 * rawGet({
 *   client,
 *   url: '/example',
 *   entity: 'example',
 * })
 *  .then(({ data }) => {
 *    console.log(data);
 *  })
 *  .catch(error => {
 *    console.error(error);
 *  });
 * ```
 *
 * Optionally you can pass a set of callbacks to `createRawAPIClient`:
 *
 * ```ts
 * const lifecycleCallbacks: RequestLifecycleCallbacks = {
 *   onNotifySuccess: (payload) => {
 *     store.dispatch(notifyAPISuccess(payload.entity));
 *   },
 *   onNotifyError: (payload, error) => {
 *     store.dispatch(notifyAPIError(payload.entity, error, payload.meta));
 *   },
 * };
 *
 * const client = createRawAPIClient({ baseURL: '/' }, lifecycleCallbacks);
 * ```
 *
 * @public
 */


var createRawAPIClient = function createRawAPIClient(config, lifecycleCallbacks) {
  if (lifecycleCallbacks === void 0) {
    lifecycleCallbacks = {};
  }

  return {
    instance: createHttpClient(config),
    lifecycleCallbacks: lifecycleCallbacks
  };
};

var apiRequest = function apiRequest(params) {
  var _a = params.client,
      instance = _a.instance,
      lifecycleCallbacks = _a.lifecycleCallbacks,
      url = params.url,
      data = params.data,
      config = params.config,
      method = params.method,
      entity = params.entity,
      shouldNotifyError = params.shouldNotifyError,
      shouldNotifySuccess = params.shouldNotifySuccess,
      validation = params.validation;
  return new Promise(function (onSuccess, onError) {
    var payload = {
      entity: entity,
      client: null,
      meta: null,
      isReloadable: false,
      request: {
        config: config,
        data: data,
        method: method,
        url: url
      },
      onSuccess: onSuccess,
      onError: onError,
      shouldNotifySuccess: shouldNotifySuccess,
      shouldNotifyError: shouldNotifyError,
      shouldStoreData: T,
      validation: validation
    };
    requestManager(instance, payload, lifecycleCallbacks);
  });
};
/**
 * Issues a `GET` request.
 *
 * Resolves with the raw `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * See also: {@link ignoreAPIRequestError}
 *
 * @param params - The request parameters, see {@link RawAPIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRes - The response body type.
 *
 * @example
 * ```ts
 * type Response = {
 *   serverResponseData: string,
 * };
 *
 * const getExample = (id: string): RequestPromise<Response> =>
 *   rawGet<Response>({
 *     client,
 *     entity: 'entity-example',
 *     url: '/example',
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * getExample('2')
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 * The example above can be used with `react-query` as follows:
 * ```ts
 * const MyComponent = ({ exampleID }) => {
 *   const { data: result, isLoading: isLoadingResult } = useQuery(
 *     ['entity-example', exampleID],
 *     async () => (await getExample(exampleID)).data
 *   );
 *
 *   return (
 *     <pre>{isLoadingResult ? 'Loading...' : data.serverResponseData}</pre>
 *   );
 * };
 * ```
 * You can use the request entity as your query key (`'entity-example'` in this case),
 * remember to also include any other request parameter (like the `exampleID` prop) in
 * the query key array.
 *
 * @public
 */


var rawGet = function rawGet(params) {
  return apiRequest(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.GET,
    data: undefined
  }));
};
/**
 * Issues a `POST` request.
 *
 * Resolves with the raw `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * See also: {@link ignoreAPIRequestError}
 *
 * @param params - The request parameters, see {@link RawAPIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRes - The response body type.
 * @typeParam TReq - The request body type.
 *
 * @example
 * ```ts
 * type Response = {
 *   serverResponseData: string,
 * };
 *
 * type Body = {
 *   clientData: string,
 * };
 *
 * const postExample = (data: Body): RequestPromise<Response> =>
 *   rawPost<Response, Body>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *   });
 *
 * postExample({ clientData: 'Example' })
 *   .then(({ data }) => {
 *     console.log('Response: ', data.serverResponseData);
 *    })
 *    .catch(ignoreAPIRequestError);
 * ```
 * The example above can be used with `react-query` as follows:
 * ```ts
 * const MyComponent = ({ clientData }) => {
 *   const [createExample, { isLoading }] = useMutation(postExample);
 *
 *   const handleOnCreateClick = useCallback(() => createExample({ clientData }), [clientData, createExample]);
 *
 *   return (
 *     <button onClick={handleOnCreateClick} disabled={isLoading}>
 *       Create Example
 *     </button>
 *   );
 * };
 * ```
 *
 * @public
 */


var rawPost = function rawPost(params) {
  return apiRequest(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.POST
  }));
};
/**
 * Issues a `PUT` request.
 *
 * Resolves with the raw `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * See also: {@link ignoreAPIRequestError}.
 *
 * @param params - The request parameters, see {@link RawAPIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRes - The response body type.
 * @typeParam TReq - The request body type.
 *
 * @example
 * ```ts
 * type Response = {
 *  serverResponseData: string,
 * };
 *
 * type Body = {
 *  clientData: string,
 * };
 *
 * const putExample = (data: Body): RequestPromise<Response> =>
 *  rawPut<Response, Body>({
 *    client,
 *    entity: 'example',
 *    url: '/example',
 *    data,
 *  });
 *
 * putExample({ clientData: 'Example' })
 *  .then(({ data }) => {
 *    console.log('Response: ', data.serverResponseData);
 *  })
 *  .catch(ignoreAPIRequestError);
 * ```
 * The example above can be used with `react-query` as follows:
 * ```ts
 * const MyComponent = ({ clientData }) => {
 *   const [replaceExample, { isLoading }] = useMutation(putExample);
 *
 *   const handleOnReplaceClick = useCallback(() => replaceExample({ clientData }), [clientData, replaceExample]);
 *
 *   return (
 *     <button onClick={handleOnReplaceClick} disabled={isLoading}>
 *       Replace Example
 *     </button>
 *   );
 * };
 * ```
 *
 * @public
 */


var rawPut = function rawPut(params) {
  return apiRequest(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.PUT
  }));
};
/**
 * Issues a `PATCH` request.
 *
 * Resolves with the raw `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * See also: {@link ignoreAPIRequestError}
 *
 * @param params - The request parameters, see {@link RawAPIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRes - The response body type.
 * @typeParam TReq - The request body type.
 *
 * @example
 * ```ts
 * type Response = {
 *  serverResponseData: string,
 * };
 *
 * type Body = {
 *  clientData: string,
 * };
 *
 * const patchExample = (data: Body): RequestPromise<Response> =>
 *   rawPatch<Response, Body>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *   });
 *
 * patchExample({ clientData: 'Example' })
 *   .then(({ data }) => {
 *     console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 * The example above can be used with `react-query` as follows:
 * ```ts
 * const MyComponent = ({ clientData }) => {
 *   const [updateExample, { isLoading }] = useMutation(patchExample);
 *
 *   const handleOnReplaceClick = useCallback(() => updateExample({ clientData }), [clientData, updateExample]);
 *
 *   return (
 *     <button onClick={handleOnReplaceClick} disabled={isLoading}>
 *       Update Example
 *     </button>
 *   );
 * };
 * ```
 *
 * @public
 */


var rawPatch = function rawPatch(params) {
  return apiRequest(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.PATCH
  }));
};
/**
 * Issues a `DELETE` request.
 *
 * Resolves with the raw `AxiosResponse`.
 * Rejects with the `AxiosError`, {@link ValidationError}, or any other internal
 * error that might happen during the request.
 *
 * See also: {@link ignoreAPIRequestError}
 *
 * @param params - The request parameters, see {@link RawAPIRequestParams} for details on how to configure the request.
 *
 * @typeParam TRes - The response body type.
 *
 * @example
 * ```ts
 * type Response = {
 *  serverResponseData: string,
 * };
 *
 * const deleteExample = (id: string): RequestPromise<Response> =>
 *   rawDelete<Response>({
 *     client,
 *     entity: 'example',
 *     url: '/example',
 *     data,
 *     config: {
 *       params: {
 *         id,
 *       },
 *     },
 *   });
 *
 * deleteExample('2')
 *   .then(({ data }) => {
 *      console.log('Response: ', data.serverResponseData);
 *   })
 *   .catch(ignoreAPIRequestError);
 * ```
 * The example above can be used with `react-query` as follows:
 * ```ts
 * const MyComponent = ({ exampleId }) => {
 *   const [removeExample, { isLoading }] = useMutation(deleteExample);
 *
 *   const handleOnDelete = useCallback(() => removeExample(exampleId), [exampleId, removeExample]);
 *
 *   return (
 *     <button onClick={handleOnDelete} disabled={isLoading}>
 *       Delete Example
 *     </button>
 *   );
 * };
 * ```
 * @public
 */


var rawDelete = function rawDelete(params) {
  return apiRequest(_assign(_assign({}, params), {
    method: API_REQUEST_HTTP_METHODS.DELETE,
    data: undefined
  }));
};

export { API_META_REDUCER_ATTRIBUTES, API_META_TYPES, API_REQUEST, API_REQUEST_HTTP_METHODS, API_STATUS_ACTION_SUFFIXES, API_STATUS_TYPES, SUCCESS_NOTIFICATION_AUTO_DISMISS, ValidationError, addEndpointInErrorMessage, addInterceptors, apiDelete, apiErrorAction, apiErrorActionType, apiErrorState, apiGet, apiInitialState, apiLoadingState, apiPatch, apiPost, apiPut, apiReducerCreator, apiRequestMiddleware, apiRequestedAction, apiRequestedActionType, apiSuccessAction, apiSuccessActionType, apiSuccessState, baseAPIRequest, convertResponseHeaders, createHttpClient, createRawAPIClient, getAPIDataSelector, getAPIDataSelectorOr, getAPIErrorSelector, getAPIHeadersSelector, getAPIRetryFunction, getData, hasErrorAPISelector, hasLoadedAPISelector, ignoreAPIRequestError, isAPIRequestAction, isAPIRequestUnauthorized, isLoadingAPISelector, isNetworkError, isTimeoutError, notifyAPIError, notifyAPISuccess, overData, rawDelete, rawGet, rawPatch, rawPost, rawPut, requestManager, setData, shouldLogError, validateAPIResponseStatus };